import { EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import { ARCHIVED_FILTER_NAME } from '../../constants/filter'
import {
  SEARCH_RESULTS_QUERY_PARAM_KEY,
  SEARCH_RESULTS_REQUESTS_QUERY_PARAM_KEY,
} from '../../constants/route'

import { resetSearchFilterState } from './SearchResults.api'
import { SEARCH_RESULTS_SESSION_FLAG_VALUE } from './SearchResults.constants'

function getSkipResetKey(plural, sub) {
  return `searchFilterSkipReset:${plural}:${sub}`
}

function getInitGuardKey({ isArchived, plural, searchQuery, sub }) {
  const archivedSuffix = isArchived ? `:${ARCHIVED_FILTER_NAME}` : EMPTY_STRING

  return `searchFilterInit:${plural}:${sub}:${searchQuery}${archivedSuffix}`
}

export function getSearchResultsBreadcrumbs({
  leadingBreadcrumbs,
  searchQuery,
  t,
}) {
  return [
    ...leadingBreadcrumbs,
    {
      id: 'search-results',
      title: t('SearchResults.SearchQueryBreadcrumbKey', {
        query: searchQuery,
      }),
    },
  ]
}

export function getSearchResultsListApiParams({ filters, page, size }) {
  // `archived` is owned by the top-level flag below (fed via `extraFilters`);
  // pulling it out keeps it from being duplicated inside the nested filters.
  const {
    [ARCHIVED_FILTER_NAME]: archived = false,
    [SEARCH_RESULTS_QUERY_PARAM_KEY]: query,
    ...restFilters
  } = filters || EMPTY_OBJECT

  return {
    [ARCHIVED_FILTER_NAME]: archived,
    [SEARCH_RESULTS_QUERY_PARAM_KEY]: query,
    filters: restFilters,
    page,
    size,
  }
}

/**
 * Marks the next search-results filter-state load (for the given table) as "do not reset".
 * Used when a shared filter link is applied: the filters were just written server-side, so the
 * destination search page must keep them instead of treating the arrival as a fresh search.
 */
export function setSearchFilterSkipReset(plural, sub) {
  sessionStorage.setItem(
    getSkipResetKey(plural, sub),
    SEARCH_RESULTS_SESSION_FLAG_VALUE
  )
}

export function consumeSkipReset(plural, sub) {
  const key = getSkipResetKey(plural, sub)
  const value = sessionStorage.getItem(key)

  if (value) {
    sessionStorage.removeItem(key)
  }

  return Boolean(value)
}

/**
 * Builds the plain options that turn the generic {@link useFilterStateLoader} into a
 * server-backed, persisted-and-shareable search-results filter state — without injecting a
 * hook. Spread the result into a table/list `asyncTableOptions`/`asyncListOptions` object:
 *
 *   - `beforeLoad` resets the server-side filter state before the load, decided up front
 *     (never load-then-reset): a fresh search (new tab, or changed query in the same tab)
 *     resets; a reload (per-tab init guard present) or a just-applied shared link (skip-reset
 *     flag) keeps the existing state. The per-tab guard lives in `sessionStorage`.
 *   - `saveExtras` persists/shares the current search term as a sibling `search_query` so the
 *     apply endpoint can rebuild the results URL.
 */
export function getSearchFilterStateOptions({
  entity,
  isArchived = false,
  searchQuery,
  sub,
}) {
  return {
    beforeLoad: () => {
      const guardKey = getInitGuardKey({
        isArchived,
        plural: entity.plural,
        searchQuery,
        sub,
      })
      const skipReset = consumeSkipReset(entity.plural, sub)
      const alreadyInitInTab = sessionStorage.getItem(guardKey)

      const ensureFresh =
        skipReset || alreadyInitInTab
          ? Promise.resolve()
          : resetSearchFilterState({ entity, sub }).catch(error =>
              console.log(error)
            )

      return ensureFresh.then(() => {
        sessionStorage.setItem(guardKey, SEARCH_RESULTS_SESSION_FLAG_VALUE)
      })
    },
    saveExtras: searchQuery
      ? { [SEARCH_RESULTS_REQUESTS_QUERY_PARAM_KEY]: searchQuery }
      : EMPTY_OBJECT,
  }
}
